import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AIChatBox from '../components/AIChatBox';
import '../styles/support.css';

const Support = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const faqs = [
        {
            category: "Compte et Inscription",
            questions: [
                {
                    q: "Comment créer un compte sur Wennze?",
                    a: "Cliquez sur 'S'inscrire', renseignez votre nom, prénom, email, téléphone et mot de passe. Vous recevrez un code de vérification par SMS pour activer votre compte. Vous pouvez aussi vous inscrire via Google."
                },
                {
                    q: "J'ai oublié mon mot de passe, que faire?",
                    a: "Sur la page de connexion, cliquez sur 'Mot de passe oublié'. Entrez votre email et vous recevrez un lien de réinitialisation. Suivez les instructions reçues par email pour créer un nouveau mot de passe."
                },
                {
                    q: "Comment supprimer mon compte?",
                    a: "Allez dans votre Profil > Paramètres > Supprimer mon compte. Vous devrez vérifier votre mot de passe. Attention: cette action est irréversible et supprime tous vos produits, messages, transactions et votre solde Zaires restant."
                },
                {
                    q: "Comment modifier mes informations personnelles?",
                    a: "Allez dans votre Profil, cliquez sur l'icône de modification. Vous pouvez changer votre photo, nom, bio, ville, province et informations de contact."
                }
            ]
        },
        {
            category: "Vendre sur Wennze",
            questions: [
                {
                    q: "Comment publier un produit?",
                    a: "Cliquez sur le bouton '+' en bas de l'écran. Ajoutez des photos (jusqu'à 10), renseignez le titre, description, prix, catégorie, province, ville et état du produit (neuf/occasion). Vous pouvez aussi ajouter des vidéos pour mieux présenter votre article."
                },
                {
                    q: "Combien coûte la publication d'une annonce?",
                    a: "La publication est gratuite! Vous pouvez poster autant de produits que vous voulez sans frais."
                },
                {
                    q: "Qu'est-ce que le boost et comment ça marche?",
                    a: "Le boost met votre annonce en avant dans les résultats de recherche pendant une durée que vous choisissez (1, 3, 7 ou 30 jours). Les produits boostés apparaissent en premier et ont un badge 'Sponsorisé'. Les prix varient selon la durée."
                },
                {
                    q: "Comment modifier ou supprimer mon annonce?",
                    a: "Allez dans votre Profil > Mes Produits. Sélectionnez le produit et choisissez 'Modifier' pour changer les détails, ou 'Supprimer' pour retirer l'annonce définitivement."
                },
                {
                    q: "Puis-je voir les statistiques de mes annonces?",
                    a: "Oui! Dans votre Coin Utilisateur, vous pouvez voir le nombre de vues, de likes, de messages reçus et d'autres statistiques pour chaque produit."
                },
                {
                    q: "Comment mettre mon produit en vedette?",
                    a: "Utilisez la fonction 'Promouvoir' sur votre produit. Vous pouvez le booster pour le mettre en avant pendant une période déterminée."
                }
            ]
        },
        {
            category: "Acheter sur Wennze",
            questions: [
                {
                    q: "Comment rechercher un produit?",
                    a: "Utilisez la barre de recherche en haut pour chercher par mot-clé. Vous pouvez aussi parcourir par catégories depuis le Dashboard. Filtrez par province, ville, catégorie, prix et état (neuf/occasion)."
                },
                {
                    q: "Comment sauvegarder mes produits préférés?",
                    a: "Cliquez sur l'icône cœur ❤️ sur un produit pour l'ajouter à vos favoris. Retrouvez tous vos produits likés dans la section 'Mes Likes'."
                },
                {
                    q: "Comment contacter un vendeur?",
                    a: "Sur la page du produit, cliquez sur 'Contacter le vendeur' ou l'icône message. Vous serez dirigé vers la messagerie pour discuter directement avec le vendeur."
                },
                {
                    q: "Comment voir les produits près de chez moi?",
                    a: "Sur le Dashboard, utilisez les filtres de province et ville pour voir uniquement les produits dans votre localisation. Vous pouvez aussi parcourir par 'Produits Province'."
                },
                {
                    q: "Comment signaler un produit suspect?",
                    a: "Sur la page du produit, cliquez sur les trois points (...) en haut à droite et sélectionnez 'Signaler'. Choisissez la raison (contenu inapproprié, arnaque, spam, etc.)."
                },
                {
                    q: "Puis-je voir qui a consulté mon profil?",
                    a: "Oui, dans votre Coin Utilisateur, vous pouvez voir les statistiques de visites sur votre profil et vos produits."
                }
            ]
        },
        {
            category: "Messagerie et Chat",
            questions: [
                {
                    q: "Comment fonctionne la messagerie?",
                    a: "Cliquez sur 'Contacter le vendeur' sur un produit pour démarrer une conversation. Vous pouvez envoyer des messages texte, des photos, des vidéos et partager votre localisation."
                },
                {
                    q: "Comment voir toutes mes conversations?",
                    a: "Allez dans 'Messages' ou 'Inbox' pour voir toutes vos conversations actives. Les messages non lus sont indiqués par un badge."
                },
                {
                    q: "Puis-je envoyer des photos dans le chat?",
                    a: "Oui! Cliquez sur l'icône photo dans la conversation pour envoyer des images depuis votre galerie ou prendre une nouvelle photo."
                },
                {
                    q: "Comment bloquer un utilisateur?",
                    a: "Dans une conversation, cliquez sur les trois points en haut et sélectionnez 'Bloquer cet utilisateur'. Vous ne recevrez plus de messages de cette personne."
                },
                {
                    q: "Comment supprimer une conversation?",
                    a: "Balayez la conversation vers la gauche dans votre liste de messages, ou ouvrez la conversation et sélectionnez 'Supprimer' dans le menu."
                },
                {
                    q: "Les messages sont-ils sauvegardés?",
                    a: "Oui, toutes vos conversations sont sauvegardées dans votre compte. Vous pouvez y accéder depuis n'importe quel appareil."
                }
            ]
        },
        {
            category: "Portefeuille et Zaires",
            questions: [
                {
                    q: "Qu'est-ce que le portefeuille Wennze?",
                    a: "Le portefeuille Wennze utilise les 'Zaires', la monnaie virtuelle de l'application. Vous pouvez utiliser vos Zaires pour booster vos annonces et accéder aux fonctionnalités premium."
                },
                {
                    q: "Comment obtenir des Zaires?",
                    a: "Vous pouvez acheter des Zaires directement depuis votre portefeuille via mobile money ou carte bancaire. Des bonus sont offerts lors de votre première inscription."
                },
                {
                    q: "Comment vérifier mon solde de Zaires?",
                    a: "Allez dans 'Portefeuille' pour voir votre solde actuel, l'historique de vos transactions et vos dépenses en Zaires."
                },
                {
                    q: "À quoi servent les Zaires?",
                    a: "Les Zaires vous permettent de booster vos produits, promouvoir vos annonces et accéder à des fonctionnalités premium pour augmenter votre visibilité."
                },
                {
                    q: "Puis-je transférer mes Zaires à un autre utilisateur?",
                    a: "Non, les Zaires sont liés à votre compte et ne peuvent pas être transférés. Ils servent uniquement pour les services Wennze."
                }
            ]
        },
        {
            category: "Réseau et Connexions",
            questions: [
                {
                    q: "Comment suivre un utilisateur?",
                    a: "Sur le profil d'un utilisateur, cliquez sur le bouton 'Suivre'. Vous verrez ses nouveaux produits et activités dans votre fil d'actualité."
                },
                {
                    q: "Comment voir mes abonnés et abonnements?",
                    a: "Allez dans votre profil et cliquez sur 'Abonnés' pour voir qui vous suit, ou 'Abonnements' pour voir les comptes que vous suivez."
                },
                {
                    q: "Comment découvrir de nouveaux utilisateurs?",
                    a: "Utilisez la section 'Découvrir' pour explorer les profils d'autres vendeurs et acheteurs près de chez vous ou dans d'autres provinces."
                },
                {
                    q: "Puis-je masquer mon profil?",
                    a: "Oui, dans les paramètres de votre profil, vous pouvez contrôler la visibilité de vos informations et choisir qui peut voir votre profil complet."
                }
            ]
        },
        {
            category: "Notifications",
            questions: [
                {
                    q: "Comment activer les notifications push?",
                    a: "Lors de la première utilisation, autorisez les notifications. Vous pouvez aussi les activer dans Paramètres > Notifications de votre téléphone."
                },
                {
                    q: "Quels types de notifications vais-je recevoir?",
                    a: "Vous recevez des notifications pour: nouveaux messages, likes sur vos produits, nouveaux abonnés, produits boostés arrivant à expiration, et mises à jour importantes."
                },
                {
                    q: "Comment gérer mes préférences de notifications?",
                    a: "Allez dans votre profil > Paramètres > Notifications pour personnaliser quels types d'alertes vous souhaitez recevoir."
                },
                {
                    q: "Puis-je désactiver certaines notifications?",
                    a: "Oui, vous pouvez activer/désactiver individuellement chaque type de notification (messages, likes, abonnés, etc.)."
                }
            ]
        },
        {
            category: "Paiement et Transactions",
            questions: [
                {
                    q: "Quels sont les moyens de paiement acceptés?",
                    a: "Wennze est une plateforme de mise en relation. Les paiements pour les produits se font directement entre acheteur et vendeur selon leur accord (mobile money, espèces, virement, etc.). Pour les services Wennze (boost, Zaires), utilisez mobile money ou carte bancaire."
                },
                {
                    q: "Wennze prend-il une commission sur les ventes?",
                    a: "Non, Wennze ne prend aucune commission sur vos ventes. Vous gardez 100% du prix de vente de vos produits. Seuls les services premium optionnels (boost) sont payants."
                },
                {
                    q: "Comment fonctionne la livraison?",
                    a: "La livraison est organisée directement entre acheteur et vendeur. Vous pouvez convenir d'un point de rencontre, d'une livraison à domicile ou utiliser un service de livraison de votre choix."
                },
                {
                    q: "Puis-je négocier les prix?",
                    a: "Oui! Utilisez la messagerie pour discuter et négocier le prix avec le vendeur. Beaucoup de vendeurs sont ouverts à la négociation."
                },
                {
                    q: "Comment payer le boost avec les Zaires?",
                    a: "Lors du boost d'un produit, sélectionnez la durée souhaitée. Le coût en Zaires sera déduit automatiquement de votre portefeuille. Assurez-vous d'avoir suffisamment de Zaires."
                }
            ]
        },
        {
            category: "Sécurité",
            questions: [
                {
                    q: "Comment éviter les arnaques?",
                    a: "Rencontrez le vendeur en personne, vérifiez le produit avant de payer, utilisez des lieux publics pour les échanges, ne payez jamais à l'avance sans garanties."
                },
                {
                    q: "Mes données sont-elles sécurisées?",
                    a: "Oui, nous utilisons le cryptage SSL et ne partageons jamais vos données personnelles avec des tiers sans votre consentement."
                },
                {
                    q: "Que faire si je suis victime d'une arnaque?",
                    a: "Signalez immédiatement l'utilisateur et le produit via l'application. Contactez-nous avec les détails pour que nous puissions prendre des mesures."
                }
            ]
        }
    ];

    return (
        <div className="support-page">
            <motion.div className="support-hero" {...fadeIn}>
                <div className="support-hero-content">
                    <h1>Centre d'Aide Wennze</h1>
                    <p>Trouvez rapidement des réponses à vos questions</p>
                </div>
            </motion.div>

            <div className="support-container">
                <motion.div 
                    className="support-quick-links"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <h2>Besoin d'aide rapide?</h2>
                    <div className="quick-links-grid">
                        <Link to="/contact" className="quick-link-card">
                            <div className="quick-link-icon">📧</div>
                            <h3>Contactez-nous</h3>
                            <p>Envoyez-nous un message</p>
                        </Link>
                        <a href="tel:+243828812498" className="quick-link-card">
                            <div className="quick-link-icon">📞</div>
                            <h3>Appelez-nous</h3>
                            <p>+243 828 812 498</p>
                        </a>
                        <a href="mailto:info@wennze.com" className="quick-link-card">
                            <div className="quick-link-icon">✉️</div>
                            <h3>Email</h3>
                            <p>info@wennze.com</p>
                        </a>
                        <Link to="/terms" className="quick-link-card">
                            <div className="quick-link-icon">📋</div>
                            <h3>Conditions</h3>
                            <p>Conditions d'utilisation</p>
                        </Link>
                    </div>
                </motion.div>

                <motion.div 
                    className="support-faq"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    <h2>Questions Fréquentes (FAQ)</h2>
                    {faqs.map((category, idx) => (
                        <div key={idx} className="faq-category">
                            <h3 className="faq-category-title">{category.category}</h3>
                            <div className="faq-questions">
                                {category.questions.map((item, qIdx) => (
                                    <details key={qIdx} className="faq-item">
                                        <summary className="faq-question">{item.q}</summary>
                                        <p className="faq-answer">{item.a}</p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    ))}
                </motion.div>

                <motion.div 
                    className="support-footer-cta"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                >
                    <h2>Vous ne trouvez pas ce que vous cherchez?</h2>
                    <p>Notre équipe est là pour vous aider</p>
                    <Link to="/contact" className="cta-button">Contactez le Support</Link>
                </motion.div>
            </div>
            
            {/* AI ChatBox Component */}
            <AIChatBox />
        </div>
    );
};

export default Support;
